// src/hooks/usePlanPermissions.js
import { useMemo } from 'react';
import { 
  obtenerPermisos, 
  puedeAcceder, 
  getTextoFuncionalidad,
  obtenerReportesDisponibles,
  obtenerLimiteMensualReportes,
  requiereReporteConsolidado
} from '../config/permisos';
import { useTranslation } from './useTranslation';

export const usePlanPermissions = (usuarioActual) => {
  const { t, idioma } = useTranslation();
  const lang = idioma === 'en' ? 'en' : 'es';
  
  const plan = useMemo(() => {
    if (!usuarioActual?.plan) return 'starter';
    const planUsuario = usuarioActual.plan.toLowerCase();
    if (planUsuario === 'gratis') return 'starter';
    return planUsuario;
  }, [usuarioActual?.plan]);
  
  const permisos = useMemo(() => obtenerPermisos(plan), [plan]);
  
  // ✅ NUEVO: Obtener reportes disponibles según el plan
  const reportesDisponibles = useMemo(() => {
    const reportes = obtenerReportesDisponibles(plan);
    // Si es starter, devolver 'todos' para que vea todo el catálogo
    if (plan === 'starter') return 'todos';
    // Para otros planes, devolver los IDs de los reportes
    return reportes.map(r => r.id);
  }, [plan]);
  
  // ✅ NUEVO: Obtener límite mensual de reportes
  const limiteMensual = useMemo(() => {
    return obtenerLimiteMensualReportes(plan);
  }, [plan]);
  
  // ✅ NUEVO: Saber si el plan requiere reporte consolidado (Starter)
  const esConsolidado = useMemo(() => {
    return requiereReporteConsolidado(plan);
  }, [plan]);
  
  // ✅ NUEVO: Función para verificar si puede generar un reporte
  const puedeGenerarReporte = (reportesUsadosEsteMes) => {
    if (limiteMensual === 'unlimited') return true;
    return reportesUsadosEsteMes < limiteMensual;
  };
  
  const puede = (funcionalidad) => {
    return puedeAcceder(plan, funcionalidad, lang);
  };
  
  const getTexto = (funcionalidad) => {
    return getTextoFuncionalidad(plan, funcionalidad, lang);
  };
  
  const getLimite = (funcionalidad) => {
    const valor = permisos[funcionalidad];
    if (typeof valor === 'object' && valor !== null) {
      if (valor.limite) return valor.limite;
      if (valor.maxProductos) return valor.maxProductos;
      if (valor.meses) return valor.meses;
      if (valor.dias) return valor.dias;
    }
    return null;
  };
  
  const getMensajeUpgrade = (funcionalidad) => {
    const textoFunc = getTexto(funcionalidad);
    if (lang === 'en') {
      return `🔒 Upgrade to ${permisos.nombre.en} to unlock: ${textoFunc}`;
    }
    return `🔒 Actualiza a ${permisos.nombre.es} para desbloquear: ${textoFunc}`;
  };
  
  return {
    plan,
    permisos,
    reportesDisponibles,   // ✅ NUEVO
    limiteMensual,         // ✅ NUEVO
    esConsolidado,         // ✅ NUEVO
    puedeGenerarReporte,   // ✅ NUEVO
    puede,
    getTexto,
    getLimite,
    getMensajeUpgrade,
    isProductionBlocked: !permisos.produccion,
    isBotBlocked: !permisos.bot?.nivel,
    isExportBlocked: !permisos.exportarCSV,
    isReportBlocked: !permisos.reportesPDF?.disponible
  };
};

